import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import {
  Users,
  Landmark,
  Mail,
  Phone,
  User,
  Upload,
  FileSpreadsheet,
  PlusCircle,
  CheckCircle,
  AlertCircle,
  Building,
  Globe,
  FileText,
  CreditCard,
  X,
  Download,
  MapPin
} from 'lucide-react';

const ModalUploadPlotsCustomersForm = () => {
  const server_app_api_php = window.SERVER_APP_API_PHP;
  const [uploadMethod, setUploadMethod] = useState('');
  const [businessLocations, setBusinessLocations] = useState([]);
  const [plots, setPlots] = useState([]);
  const [formData, setFormData] = useState({
    customersLastName: '',
    customersFirstName: '',
    customersGender: '',
    customersPhone: '',
    customersEmail: '',
    customersNationality: '',
    customersNRC: '',
    plotNumberID: '',
    business_locationIDPlotsCustomers: '',
    business_locationIDPlotsCustomersExcel: '',
    excelPlotsCustomers: null,
  });
  const [companyID] = useState(1);
  const [businessLocationID, setBusinessLocationID] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [selectedPlotDetails, setSelectedPlotDetails] = useState(null);

  useEffect(() => {
    fetchBusinessLocations();
  }, []);

  useEffect(() => {
    if (businessLocationID) {
      fetchPlots();
    }
  }, [businessLocationID]);

  const fetchBusinessLocations = async () => {
    const formData = new FormData();
    formData.append("apiType", 'getBusinessLocationPlotsOnUploadForm');
    formData.append("companyID", companyID);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      setBusinessLocations(response.data);
    } catch (error) {
      console.error("Error fetching business locations:", error);
    }
  };

  const fetchPlots = async () => {
    const formData = new FormData();
    formData.append("apiType", 'getPlotsOnUploadForm');
    formData.append("businessLocationID", businessLocationID);
    
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formData);
      setPlots(response.data);
    } catch (error) {
      console.error("Error fetching plots:", error);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id]: value });
    
    if (id === 'plotNumberID') {
      const plot = plots.find(p => p.plotsID === value);
      setSelectedPlotDetails(plot);
    } else if (id === 'business_locationIDPlotsCustomers') {
      setBusinessLocationID(value);
    }
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, excelPlotsCustomers: e.target.files[0] });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZM', { style: 'currency', currency: 'ZMW' }).format(amount);
  };

  const handleSubmitSingleForm = async () => {
    setIsLoading(true);
    setSubmitStatus({ type: '', message: '' });

    const formDataToSend = new FormData();
    formDataToSend.append('apiType', 'registerPlotsCustomers');
    formDataToSend.append('customersLastName', formData.customersLastName);
    formDataToSend.append('customersFirstName', formData.customersFirstName);
    formDataToSend.append('customersGender', formData.customersGender);
    formDataToSend.append('customersPhone', formData.customersPhone);
    formDataToSend.append('customersEmail', formData.customersEmail);
    formDataToSend.append('customersNationality', formData.customersNationality);
    formDataToSend.append('customersNRC', formData.customersNRC);
    formDataToSend.append('plotNumber', formData.plotNumberID);
    formDataToSend.append('companyID', companyID);
    formDataToSend.append('businessLocationID', businessLocationID);
    formDataToSend.append('business_locationIDPlotsCustomers', formData.business_locationIDPlotsCustomers);

    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formDataToSend);
      
      if (response.data === 'Successful') {
        setSubmitStatus({ 
          type: 'success', 
          message: 'Plot customer registered successfully!' 
        });
        setFormData({
          customersLastName: '',
          customersFirstName: '',
          customersGender: '',
          customersPhone: '',
          customersEmail: '',
          customersNationality: '',
          customersNRC: '',
          plotNumberID: '',
          business_locationIDPlotsCustomers: '',
          business_locationIDPlotsCustomersExcel: '',
          excelPlotsCustomers: null,
        });
        setSelectedPlotDetails(null);
      } else {
        setSubmitStatus({ 
          type: 'error', 
          message: response.data 
        });
      }
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'There seems to be a problem with your internet connectivity. If this problem continues, kindly contact the support team.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitFileUpload = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmitStatus({ type: '', message: '' });

    if (!formData.excelPlotsCustomers) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Please select an Excel file.' 
      });
      setIsLoading(false);
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('excelPlotsCustomers', formData.excelPlotsCustomers);
    formDataToSend.append('companyID', companyID);
    formDataToSend.append('businessLocationID', businessLocationID);
    formDataToSend.append('apiType', 'registerPlotsCustomersExcel');
    formDataToSend.append('business_locationIDPlotsCustomersExcel', formData.business_locationIDPlotsCustomersExcel);

    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, formDataToSend);
      
      setSubmitStatus({ 
        type: 'success', 
        message: response.data 
      });
      setFormData({
        ...formData,
        excelPlotsCustomers: null,
        business_locationIDPlotsCustomersExcel: ''
      });
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'There seems to be a problem with your internet connectivity. If this problem continues, kindly contact the support team.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isSingleFormValid = formData.customersLastName && formData.customersFirstName && 
    formData.customersGender && formData.customersPhone && formData.customersEmail && 
    formData.customersNationality && formData.customersNRC && formData.plotNumberID && 
    formData.business_locationIDPlotsCustomers;

  return (
    <>
      <Header />
      
      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-16 pl-64">
        <div className="p-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl">
                <Users className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Register Plot Customers</h1>
                <p className="text-gray-600 mt-2">Register new customers for your plots</p>
              </div>
            </div>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Form */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                  {/* Form Header */}
                  <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center space-x-3">
                      <PlusCircle className="w-6 h-6 text-blue-500" />
                      <h2 className="text-xl font-bold text-gray-900">New Plot Customer</h2>
                    </div>
                    <p className="text-gray-600 mt-2">Choose your preferred method to register customers</p>
                  </div>

                  {/* Form Content */}
                  <div className="p-8">
                    {submitStatus.message && (
                      <div className={`mb-6 p-4 rounded-xl ${
                        submitStatus.type === 'success' 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-red-50 border border-red-200'
                      }`}>
                        <div className="flex items-center">
                          {submitStatus.type === 'success' ? (
                            <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                          )}
                          <p className={submitStatus.type === 'success' ? 'text-green-700' : 'text-red-700'}>
                            {submitStatus.message}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Upload Method Selection */}
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Upload Method</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <button
                          onClick={() => setUploadMethod('Single Form')}
                          className={`p-6 rounded-xl border-2 transition-all duration-300 ${
                            uploadMethod === 'Single Form'
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                          }`}
                        >
                          <div className="flex flex-col items-center text-center">
                            <User className={`w-8 h-8 mb-3 ${
                              uploadMethod === 'Single Form' ? 'text-blue-500' : 'text-gray-400'
                            }`} />
                            <h4 className="font-medium text-gray-900">Single Form</h4>
                            <p className="text-sm text-gray-600 mt-1">Register one customer at a time</p>
                          </div>
                        </button>
                        <button
                          onClick={() => setUploadMethod('Upload Excel File')}
                          className={`p-6 rounded-xl border-2 transition-all duration-300 ${
                            uploadMethod === 'Upload Excel File'
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                          }`}
                        >
                          <div className="flex flex-col items-center text-center">
                            <FileSpreadsheet className={`w-8 h-8 mb-3 ${
                              uploadMethod === 'Upload Excel File' ? 'text-green-500' : 'text-gray-400'
                            }`} />
                            <h4 className="font-medium text-gray-900">Excel Upload</h4>
                            <p className="text-sm text-gray-600 mt-1">Bulk upload with Excel file</p>
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* Single Form */}
                    {uploadMethod === 'Single Form' && (
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Business Location */}
                          <div className="md:col-span-2">
                            <label htmlFor="business_locationIDPlotsCustomers" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <Building className="w-4 h-4 mr-2 text-gray-400" />
                                Business Location *
                              </div>
                            </label>
                            <select
                              id="business_locationIDPlotsCustomers"
                              value={formData.business_locationIDPlotsCustomers}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            >
                              <option value="">Select location...</option>
                              {businessLocations.map(location => (
                                <option key={location.businessLocationID} value={location.businessLocationID}>
                                  {location.businessLocationName}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Last Name */}
                          <div>
                            <label htmlFor="customersLastName" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <User className="w-4 h-4 mr-2 text-gray-400" />
                                Last Name *
                              </div>
                            </label>
                            <input
                              type="text"
                              id="customersLastName"
                              value={formData.customersLastName}
                              onChange={handleInputChange}
                              placeholder="Enter last name"
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            />
                          </div>

                          {/* First Name */}
                          <div>
                            <label htmlFor="customersFirstName" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <User className="w-4 h-4 mr-2 text-gray-400" />
                                First Name *
                              </div>
                            </label>
                            <input
                              type="text"
                              id="customersFirstName"
                              value={formData.customersFirstName}
                              onChange={handleInputChange}
                              placeholder="Enter first name"
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            />
                          </div>

                          {/* Gender */}
                          <div>
                            <label htmlFor="customersGender" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <Users className="w-4 h-4 mr-2 text-gray-400" />
                                Gender *
                              </div>
                            </label>
                            <select
                              id="customersGender"
                              value={formData.customersGender}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            >
                              <option value="">Select gender...</option>
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                            </select>
                          </div>

                          {/* Phone */}
                          <div>
                            <label htmlFor="customersPhone" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <Phone className="w-4 h-4 mr-2 text-gray-400" />
                                Phone Number *
                              </div>
                            </label>
                            <input
                              type="tel"
                              id="customersPhone"
                              value={formData.customersPhone}
                              onChange={handleInputChange}
                              placeholder="+260 XXX XXX XXX"
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            />
                          </div>

                          {/* Email */}
                          <div>
                            <label htmlFor="customersEmail" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <Mail className="w-4 h-4 mr-2 text-gray-400" />
                                Email Address *
                              </div>
                            </label>
                            <input
                              type="email"
                              id="customersEmail"
                              value={formData.customersEmail}
                              onChange={handleInputChange}
                              placeholder="customer@example.com"
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            />
                          </div>

                          {/* Nationality */}
                          <div>
                            <label htmlFor="customersNationality" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <Globe className="w-4 h-4 mr-2 text-gray-400" />
                                Nationality *
                              </div>
                            </label>
                            <input
                              type="text"
                              id="customersNationality"
                              value={formData.customersNationality}
                              onChange={handleInputChange}
                              placeholder="e.g., Zambian, Zimbabwean"
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            />
                          </div>

                          {/* NRC */}
                          <div className="md:col-span-2">
                            <label htmlFor="customersNRC" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <FileText className="w-4 h-4 mr-2 text-gray-400" />
                                NRC Number *
                              </div>
                            </label>
                            <input
                              type="text"
                              id="customersNRC"
                              value={formData.customersNRC}
                              onChange={handleInputChange}
                              placeholder="Enter NRC number"
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            />
                          </div>

                          {/* Plot Selection */}
                          <div className="md:col-span-2">
                            <label htmlFor="plotNumberID" className="block text-sm font-medium text-gray-700 mb-2">
                              <div className="flex items-center">
                                <Landmark className="w-4 h-4 mr-2 text-gray-400" />
                                Plot Selection *
                              </div>
                            </label>
                            <select
                              id="plotNumberID"
                              value={formData.plotNumberID}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                              required
                            >
                              <option value="">Select plot...</option>
                              {plots.map((plot, index) => (
                                <option key={plot.plotsID} value={plot.plotsID}>
                                  Plot {plot.plotNumber} - {formatCurrency(plot.plotPrice)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Selected Plot Details */}
                        {selectedPlotDetails && (
                          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-xl">
                            <h4 className="font-medium text-gray-900 mb-3">Selected Plot Details</h4>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs text-gray-600">Plot Number</p>
                                <p className="font-medium text-gray-900">{selectedPlotDetails.plotNumber}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600">Plot Price</p>
                                <p className="font-medium text-green-600">{formatCurrency(selectedPlotDetails.plotPrice)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600">Plot Area</p>
                                <p className="font-medium text-gray-900">{selectedPlotDetails.plotArea || 'N/A'} m²</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-600">Location</p>
                                <p className="font-medium text-gray-900">{selectedPlotDetails.businessLocationName}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Submit Button */}
                        <div className="pt-6">
                          <button
                            onClick={handleSubmitSingleForm}
                            disabled={!isSingleFormValid || isLoading}
                            className={`w-full py-3.5 px-6 rounded-xl font-medium transition-all duration-300 ${
                              isSingleFormValid && !isLoading
                                ? 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white shadow-lg hover:shadow-xl'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {isLoading ? (
                              <div className="flex items-center justify-center">
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                                Registering Customer...
                              </div>
                            ) : (
                              <div className="flex items-center justify-center">
                                <PlusCircle className="w-5 h-5 mr-2" />
                                Register Customer
                              </div>
                            )}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Excel Upload Form */}
                    {uploadMethod === 'Upload Excel File' && (
                      <form onSubmit={handleSubmitFileUpload} className="space-y-6">
                        {/* Business Location */}
                        <div>
                          <label htmlFor="business_locationIDPlotsCustomersExcel" className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center">
                              <Building className="w-4 h-4 mr-2 text-gray-400" />
                              Business Location *
                            </div>
                          </label>
                          <select
                            id="business_locationIDPlotsCustomersExcel"
                            value={formData.business_locationIDPlotsCustomersExcel}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                            required
                          >
                            <option value="">Select location...</option>
                            {businessLocations.map(location => (
                              <option key={location.businessLocationID} value={location.businessLocationID}>
                                {location.businessLocationName}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* File Upload */}
                        <div>
                          <label htmlFor="excelPlotsCustomers" className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center">
                              <FileSpreadsheet className="w-4 h-4 mr-2 text-gray-400" />
                              Excel File *
                            </div>
                          </label>
                          
                          <div className="relative">
                            <input
                              type="file"
                              id="excelPlotsCustomers"
                              name="excelPlotsCustomers"
                              onChange={handleFileChange}
                              accept=".xlsx,.xls,.csv"
                              className="hidden"
                              required
                            />
                            <label
                              htmlFor="excelPlotsCustomers"
                              className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all duration-300"
                            >
                              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                {formData.excelPlotsCustomers ? (
                                  <>
                                    <FileSpreadsheet className="w-8 h-8 text-green-500 mb-2" />
                                    <p className="text-sm font-medium text-gray-900">{formData.excelPlotsCustomers.name}</p>
                                    <p className="text-xs text-gray-500 mt-1">{(formData.excelPlotsCustomers.size / 1024).toFixed(2)} KB</p>
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                                    <p className="text-sm text-gray-500">
                                      <span className="font-medium text-blue-500">Click to upload</span> or drag and drop
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1">
                                      Excel files only (.xlsx, .xls, .csv)
                                    </p>
                                  </>
                                )}
                              </div>
                            </label>
                            {formData.excelPlotsCustomers && (
                              <button
                                type="button"
                                onClick={() => setFormData({...formData, excelPlotsCustomers: null})}
                                className="absolute top-2 right-2 p-1 bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition-colors"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Submit Button */}
                        <div className="pt-6">
                          <button
                            type="submit"
                            disabled={!formData.business_locationIDPlotsCustomersExcel || !formData.excelPlotsCustomers || isLoading}
                            className={`w-full py-3.5 px-6 rounded-xl font-medium transition-all duration-300 ${
                              formData.business_locationIDPlotsCustomersExcel && formData.excelPlotsCustomers && !isLoading
                                ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-lg hover:shadow-xl'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {isLoading ? (
                              <div className="flex items-center justify-center">
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                                Uploading File...
                              </div>
                            ) : (
                              <div className="flex items-center justify-center">
                                <Upload className="w-5 h-5 mr-2" />
                                Upload Excel File
                              </div>
                            )}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>

                  {/* Form Footer */}
                  <div className="px-8 py-6 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <AlertCircle className="w-4 h-4 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Customer Privacy</p>
                          <p className="text-xs text-gray-500">All customer data is securely stored</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">
                          {uploadMethod === 'Single Form' ? '8 required fields' : '2 required fields'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Guidelines & Info */}
              <div className="space-y-8">
                {/* Required Documents */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-blue-500" />
                    Required Information
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700">Valid NRC number for Zambian citizens</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700">Active phone number for SMS notifications</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700">Valid email for electronic receipts</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-700">Complete plot selection details</span>
                    </li>
                  </ul>
                </div>

                {/* Quick Stats */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <h3 className="font-bold text-gray-900 flex items-center">
                      <Landmark className="w-5 h-5 mr-2 text-blue-500" />
                      Registration Stats
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Building className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">Available Locations</span>
                        </div>
                        <span className="font-medium text-gray-900">{businessLocations.length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">Available Plots</span>
                        </div>
                        <span className="font-medium text-gray-900">{plots.length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Users className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600">Upload Methods</span>
                        </div>
                        <span className="font-medium text-gray-900">2</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Download Template */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <h3 className="font-bold text-gray-900 flex items-center">
                      <Download className="w-5 h-5 mr-2 text-green-500" />
                      Download Template
                    </h3>
                  </div>
                  <div className="p-6">
                    <button className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all duration-300">
                      <Download className="w-5 h-5" />
                      <span>Download Excel Template</span>
                    </button>
                    <p className="text-xs text-gray-500 mt-3 text-center">
                      Use this template for bulk customer uploads
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default ModalUploadPlotsCustomersForm;